import React from 'react';
import Autocomplete, {
  AutocompleteRenderInputParams
} from '@material-ui/lab/Autocomplete';
import {
  TextField,
  CircularProgress,
  IconButton,
  Box
} from '@material-ui/core';
import SearchIcon from '@material-ui/icons/Search';
import ClearIcon from '@material-ui/icons/Clear';

interface OptionType {
  displayName: string;
  id?: number;
  resultStatus?: string;
}

interface AutocompleteSearchProps {
  isClient?: boolean;
  placeholder: string;
  search: string;
  isSearchLoading: boolean;
  searchSuggestions: OptionType[];
  searchOpen: boolean;
  isSearchSelected: boolean;
  onInputChange: (event: any, value: string, reason: string) => void;
  onChange: (event: any, value: OptionType | string | null) => void;
  onClear: () => void;
  onOpen?: () => void;
  onClose?: () => void;
}

const AutocompleteSearch: React.FC<AutocompleteSearchProps> = ({
  isClient,
  placeholder,
  search,
  isSearchLoading,
  searchSuggestions,
  searchOpen,
  isSearchSelected,
  onInputChange,
  onChange,
  onClear,
  onOpen,
  onClose
}) => {
  return (
    <>
      <style>{`
        .custom-autocomplete-textfield .MuiOutlinedInput-root.Mui-focused .MuiOutlinedInput-notchedOutline {
          border-color: #7B61FF !important;
          border-width: 2px !important;
        }
      `}</style>
      <Autocomplete
        inputValue={search}
        options={searchSuggestions}
        loading={isSearchLoading}
        open={searchOpen}
        onOpen={onOpen}
        onClose={onClose}
        disableCloseOnSelect={false}
        selectOnFocus
        clearOnBlur={false}
        value={null}
        getOptionLabel={(option: OptionType | string) => {
          if (typeof option === 'string') return option;
          return option.displayName || '';
        }}
        getOptionDisabled={(option: OptionType | string) => {
          if (typeof option === 'string') return false;
          return option.resultStatus === '__NO_RESULTS__';
        }}
        onChange={(event, value) => {
          onChange(event, value);
        }}
        onInputChange={onInputChange}
        freeSolo
        renderInput={(params: AutocompleteRenderInputParams) => (
          <TextField
            {...params}
            placeholder={placeholder}
            variant="outlined"
            fullWidth
            InputProps={{
              ...params.InputProps,
              endAdornment: (
                <>
                  {isSearchLoading ? (
                    <CircularProgress color="inherit" size={20} />
                  ) : isSearchSelected ? (
                    <IconButton
                      size="small"
                      tabIndex={-1}
                      onClick={onClear}
                      style={{ padding: 15 }}
                    >
                      <ClearIcon style={{ color: '#8A8A8A', fontSize: 22 }} />
                    </IconButton>
                  ) : (
                    <IconButton
                      size="small"
                      tabIndex={-1}
                      style={{ padding: 15 }}
                    >
                      <SearchIcon style={{ color: '#8A8A8A', fontSize: 22 }} />
                    </IconButton>
                  )}
                </>
              ),
              style: {
                borderRadius: 4,
                height: 55,
                background: '#fff',
                fontSize: 16,
                boxShadow: 'none',
                color: '#222',
                paddingRight: 0,
                padding: 0
              }
            }}
            inputProps={{
              ...params.inputProps,
              style: {
                color: '#222',
                fontSize: 16,
                padding: '12px 16px',
                borderRadius: 8
              }
            }}
          />
        )}
        renderOption={(option: OptionType | string) => {
          if (typeof option === 'string') return option;
          if (option.resultStatus === '__NO_RESULTS__') {
            return (
              <Box
                style={{
                  color: '#666',
                  fontStyle: 'italic',
                  pointerEvents: 'none'
                }}
              >
                {isClient ? 'No clients found' : 'No BTs found'}
              </Box>
            );
          }
          return option.displayName;
        }}
        noOptionsText={
          search.length >= 3
            ? isClient
              ? 'No clients found'
              : 'No BTs found'
            : 'Type at least 3 characters to search'
        }
        loadingText="Searching..."
        filterOptions={(options: (OptionType | string)[]) => options}
      />
    </>
  );
};

export default AutocompleteSearch;
